import React from 'react';
import { CATEGORY_BADGE_LABELS, RULE_CATEGORIES } from '@engine/index';
import { t } from '../lib/i18n';
import Icon from '../components/Icon';
import { auditPresentation, reportForPresentation } from '../lib/scoring';
import { isResolvedAck } from '../lib/findingAck';

/**
 * VerdictBanner — Score gauge + verdict text + category pills.
 * Displayed at top of ScreenReport as the primary audit result indicator.
 *
 * Extracted from index.html (previously inline ~lines 1070-1123).
 *
 * @param {{ report: { score: number, errCount: number, warnCount: number, categorySummary?: object } }} props
 */
export default function VerdictBanner({ report: storedReport, getAck }) {
    const report = reportForPresentation(storedReport);
    const { errCount, warnCount, categorySummary = {} } = report;
    const { score, unchecked, signals, limited, gaugeColor, verdictKey } = auditPresentation(report, getAck);
    // V2 thresholds: ≥8.5 success, ≥6 warning, ≥4 needs-fix, <4 critical.
    const circumference = 2 * Math.PI * 52;
    const offset = circumference - (score / 10) * circumference;
    const borderColor = score >= 8.5 && limited ? 'border-slate-300 dark:border-slate-700' : score >= 8.5 ? 'border-emerald-300 dark:border-emerald-700' : score >= 6 ? 'border-amber-300 dark:border-amber-700' : 'border-red-300 dark:border-red-700';
    const bgColor = score >= 8.5 && limited ? 'bg-slate-50 dark:bg-slate-900/20' : score >= 8.5 ? 'bg-emerald-50 dark:bg-emerald-900/20' : score >= 6 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-red-50 dark:bg-red-900/20';

    const verdictText = t(verdictKey, errCount);

    return (
        <div className={`verdict-banner animate-fade-in-up p-6 rounded-2xl shadow-lg border-2 mb-8 ${bgColor} ${borderColor}`}>
            <div className="flex flex-col md:flex-row items-center gap-6">
                {/* Score Gauge */}
                <svg width="120" height="120" viewBox="0 0 120 120" className="flex-shrink-0">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="8" className="text-slate-200 dark:text-slate-700" />
                    <circle cx="60" cy="60" r="52" fill="none" stroke={gaugeColor} strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={circumference} strokeDashoffset={offset}
                        transform="rotate(-90 60 60)" className="gauge-ring" />
                    <text x="60" y="56" textAnchor="middle" dominantBaseline="central" fill={gaugeColor}
                        className="font-heading" style={{ fontSize: '36px', fontWeight: 700 }}>{score}</text>
                    <text x="60" y="78" textAnchor="middle" fill="currentColor"
                        className="text-slate-400 dark:text-slate-500" style={{ fontSize: '12px' }}>/10</text>
                </svg>

                {/* Verdict text */}
                <div className="flex-1 text-center md:text-left">
                    <h2 className="text-2xl font-bold font-heading dark:text-white mb-2">{t('rule_scoring_title', score)}</h2>
                    <p className="text-base text-slate-700 dark:text-slate-200 mb-2">{verdictText}</p>
                    {report.presentationRetiredRules?.length > 0 && <p className="text-sm mb-2" data-history-notice="true">{t('audit_history_notice')}</p>}
                    {signals > 0 && <p className="text-sm mb-2">{t('audit_review_notice', signals)}</p>}
                    {unchecked > 0 && <p className="text-sm mb-2">{t('audit_coverage_notice', unchecked)}</p>}
                    {/* P1 fix (2026-04-25): YoY comparison widget — engine ma yoy_equity_decline + yoy_debt_increase reguły */}
                    {report && report.previousPeriod && typeof report.previousPeriod.score === 'number' && (() => {
                        const prevScore = report.previousPeriod.score;
                        const delta = score - prevScore;
                        const deltaAbs = Math.abs(delta).toFixed(1);
                        const isUp = delta > 0;
                        const noChange = Math.abs(delta) < 0.05;
                        const trendColor = noChange ? 'text-slate-500 dark:text-slate-400' : isUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
                        const trendIcon = noChange ? 'minus' : isUp ? 'trending-up' : 'trending-down';
                        return (
                            <div className={`text-sm flex items-center gap-2 mb-3 justify-center md:justify-start ${trendColor}`}>
                                <Icon name={trendIcon} className="w-4 h-4" />
                                <span className="font-semibold">
                                    {noChange ? t('yoy_no_change') : `${isUp ? '+' : '-'}${deltaAbs}`}
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {t('vs_previous_period', report.previousPeriod.label || '')}
                                </span>
                            </div>
                        );
                    })()}

                    {/* Category pills — status icon + dot (P2 fix 2026-04-25 — WCAG SC 1.4.1 fix: nie polegamy tylko na kolorze) */}
                    <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                        {RULE_CATEGORIES.map(cat => {
                            const cs = categorySummary[cat.id];
                            if (!cs || (cs.total === 0 && !cs.reviewSignals && !cs.unchecked)) return null;
                            const isError = cs.errors > 0;
                            const isWarning = !isError && cs.warnings > 0;
                            const pendingSignals = report.issues ? report.issues.filter(i => i.category === cat.id && i.specialType === 'review_signal' && !isResolvedAck(getAck?.(i.ruleId, i))).length : cs.reviewSignals || 0;
                            const incomplete = pendingSignals > 0 || cs.unchecked > 0;
                            const dotColor = !isError && !isWarning && incomplete ? 'bg-slate-500' : isError ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500';
                            const iconName = !isError && !isWarning && incomplete ? 'info' : isError ? 'x-circle' : isWarning ? 'alert-triangle' : 'check-circle';
                            const iconColor = !isError && !isWarning && incomplete ? 'text-slate-500' : isError ? 'text-red-600 dark:text-red-400' : isWarning ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
                            const ariaLabel = !isError && !isWarning && incomplete ? t('audit_score_limited') : isError ? t('status_errors') || 'Errors' : isWarning ? t('status_warnings') || 'Warnings' : t('status_ok') || 'OK';
                            return (
                                <span key={cat.id}
                                      role="status"
                                      aria-label={`${t(CATEGORY_BADGE_LABELS[cat.id] || cat.labelKey)}: ${ariaLabel}, ${cs.errors + cs.warnings} av ${cs.total}`}
                                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                                    <span className={`w-2 h-2 rounded-full ${dotColor}`} aria-hidden="true"></span>
                                    <Icon name={iconName} className={`w-3.5 h-3.5 ${iconColor}`} aria-hidden="true" />
                                    {t(CATEGORY_BADGE_LABELS[cat.id] || cat.labelKey)} {cs.errors + cs.warnings}/{cs.total}{incomplete ? ' · ' + t('audit_unresolved_count', pendingSignals + (cs.unchecked || 0)) : ''}
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
